import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { trpc } from "@/lib/trpc";

// Categorias suportadas. Para adicionar uma nova, basta acrescentar aqui +
// usar `notify(category, ...)` no ponto de origem.
export const NOTIFICATION_CATEGORIES = [
  "new_message",
  "handoff_to_human",
  "assigned_to_me",
] as const;
export type NotificationCategory = (typeof NOTIFICATION_CATEGORIES)[number];

// Whitelist runtime usada ao parsear o CSV do admin. Sem esse Set, qualquer
// string gravada por engano em `notification_categories_disabled` viraria
// uma categoria órfã no Set — fonte de bug silencioso ("desabilitei e ainda
// notifica" ou vice-versa).
const VALID_CATEGORIES: Set<string> = new Set(NOTIFICATION_CATEGORIES);

const STORAGE_KEY_PREFIX = "desktopNotifPrefs.v1";

type UserPrefs = Record<NotificationCategory, boolean>;

const DEFAULT_PREFS: UserPrefs = {
  new_message: true,
  handoff_to_human: true,
  assigned_to_me: true,
};

function readPrefs(userId: number | string | null | undefined): UserPrefs {
  if (typeof window === "undefined") return DEFAULT_PREFS;
  try {
    const raw = localStorage.getItem(`${STORAGE_KEY_PREFIX}.${userId ?? "anon"}`);
    if (!raw) return DEFAULT_PREFS;
    const parsed = JSON.parse(raw);
    return { ...DEFAULT_PREFS, ...parsed };
  } catch {
    return DEFAULT_PREFS;
  }
}

function writePrefs(userId: number | string | null | undefined, prefs: UserPrefs) {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(`${STORAGE_KEY_PREFIX}.${userId ?? "anon"}`, JSON.stringify(prefs));
  } catch { /* localStorage cheio ou desabilitado — ignorar */ }
}

// Permission state derivado de Notification.permission, com fallback seguro
// quando a API não está disponível (Safari iOS, contexto inseguro).
type PermissionState = "default" | "granted" | "denied" | "unsupported";

function readPermission(): PermissionState {
  if (typeof window === "undefined" || !("Notification" in window)) return "unsupported";
  return Notification.permission as PermissionState;
}

/**
 * Hook que gerencia preferências e o estado de permissão das notificações
 * desktop. Não dispara as notificações sozinho — exporta `notify()` que o
 * caller usa quando detecta um evento relevante (mensagem nova no polling
 * do React Query, etc).
 *
 * Preferências por categoria são guardadas em localStorage (escopo por
 * userId). Admin pode desabilitar categorias globalmente via setting
 * `notification_categories_disabled` (CSV) — neste caso `notify()` retorna
 * sem disparar mesmo se o usuário deixou ligado.
 */
export function useDesktopNotifications(userId?: number | null) {
  const [permission, setPermission] = useState<PermissionState>(readPermission());
  const [prefs, setPrefs] = useState<UserPrefs>(() => readPrefs(userId));

  // Lê a lista de categorias desabilitadas pelo admin (CSV em settings).
  // Quando vazio/ausente, tudo liberado para o usuário escolher.
  // Procedure dedicada em vez de `settings.get`: aquela exige settings:view,
  // que o atendente comum não tem. Além do toast "Sem permissão" a cada
  // montagem, o efeito pior era silencioso — sem conseguir ler, o cliente caía
  // no default "nada desabilitado" e o agente seguia recebendo categoria que o
  // admin tinha desligado. A política governa todo mundo, então todo mundo
  // precisa poder lê-la.
  const { data: policy } = trpc.settings.getNotificationPolicy.useQuery(undefined, {
    staleTime: 30_000,
  });
  const adminDisabledCsv = policy?.disabledCsv;

  // Memoização chave: `adminDisabled` é re-derivado SÓ quando o CSV muda.
  // Antes era recalculado em todo render, o que dava referência nova ao
  // `useCallback(notify, [...])` e fazia o useEffect downstream rodar a
  // cada render do Inbox. Filtro contra whitelist evita lixo (S8).
  const adminDisabled: Set<NotificationCategory> = useMemo(() => {
    const s = new Set<NotificationCategory>();
    if (typeof adminDisabledCsv === "string" && adminDisabledCsv) {
      for (const part of adminDisabledCsv.split(",")) {
        const trimmed = part.trim();
        if (VALID_CATEGORIES.has(trimmed)) s.add(trimmed as NotificationCategory);
      }
    }
    return s;
  }, [adminDisabledCsv]);

  // Re-sincroniza prefs quando o userId muda (login/logout)
  useEffect(() => {
    setPrefs(readPrefs(userId));
  }, [userId]);

  const requestPermission = useCallback(async () => {
    if (!("Notification" in window)) return "unsupported" as const;
    const result = await Notification.requestPermission();
    setPermission(result as PermissionState);
    return result;
  }, []);

  const updatePref = useCallback((category: NotificationCategory, enabled: boolean) => {
    setPrefs((prev) => {
      const next = { ...prev, [category]: enabled };
      writePrefs(userId, next);
      return next;
    });
  }, [userId]);

  // Dispara uma notificação caso (1) permissão concedida, (2) categoria
  // habilitada pelo usuário e (3) categoria não desabilitada pelo admin.
  // `tag` faz com que mensagens consecutivas da mesma conversa substituam
  // a notificação anterior em vez de empilhar.
  const notify = useCallback(
    (
      category: NotificationCategory,
      options: {
        title: string;
        body?: string;
        tag?: string;
        icon?: string;
        onClick?: () => void;
      },
    ) => {
      if (permission !== "granted") return;
      if (!prefs[category]) return;
      if (adminDisabled.has(category)) return;
      // Não notificar se a aba está visível — o usuário já vê o app.
      // Antes a heurística usava `document.hasFocus()` também, mas isso
      // dá false quando o navegador está em foco em outra janela do MESMO
      // browser — comportamento esperado de notificações é justamente
      // disparar nesse caso. `visibilityState` sozinho é suficiente.
      if (typeof document !== "undefined" && document.visibilityState === "visible") return;
      try {
        const n = new Notification(options.title, {
          body: options.body,
          tag: options.tag,
          icon: options.icon ?? "/favicon.ico",
        });
        if (options.onClick) {
          n.onclick = () => {
            window.focus();
            options.onClick?.();
            n.close();
          };
        }
      } catch (err) {
        console.warn("[useDesktopNotifications] Falha ao criar Notification:", err);
      }
    },
    [permission, prefs, adminDisabled],
  );

  return {
    permission,
    prefs,
    adminDisabled,
    requestPermission,
    updatePref,
    notify,
    isSupported: permission !== "unsupported",
  };
}

// Contrato mínimo do hook de inbound: só os campos efetivamente usados.
// Tipar isso explicitamente evita os `as any` no call-site (Inbox.tsx) e
// trava o contrato para que mudanças no shape da Conversation quebrem o
// build em vez de virar bug em runtime.
export type InboundConversation = {
  id: number;
  contactName: string | null;
  contactPhone: string | null;
  lastMessageAt: Date | string | null;
  lastMessageContent: string | null;
  unreadCount: number;
};

/**
 * Detecta mensagens inbound novas chegando em `listConversations` (polling)
 * e dispara notificações desktop quando relevante. Usar uma vez no Inbox
 * (ou shell do app) — não renderiza nada.
 *
 * Bootstrap: só marca como "inicializado" depois que a primeira lista de
 * conversas chega de fato (length > 0). Antes era no primeiro effect run,
 * o que disparava spam no login porque o array vinha vazio (loading) e o
 * tick seguinte tratava TODAS as conversas com unread como "novas".
 */
export function useInboundMessageNotifications(args: {
  userId: number | null;
  conversations: InboundConversation[] | undefined | null;
  onOpenConversation?: (conversationId: number) => void;
}) {
  const { userId, onOpenConversation } = args;
  // Tolera undefined/null mesmo que o caller default `= []` — defesa em
  // profundidade para o caso de a query falhar (500) e o tipo inferido do
  // tRPC vir undefined. Lição do incidente do PR #202/#203: confiar só no
  // default do destructuring expôs o app a erros estruturais do backend.
  const conversations: InboundConversation[] = Array.isArray(args.conversations)
    ? args.conversations
    : [];
  const { notify } = useDesktopNotifications(userId ?? null);

  // Snapshot da última lastMessageAt observada por conversa.
  const lastSeenRef = useRef<Map<number, number>>(new Map());
  const bootstrappedRef = useRef(false);

  useEffect(() => {
    // Bootstrap robusto: ignora ticks de loading (length === 0). Caso o
    // tenant realmente tenha 0 conversas, o primeiro tick com dados que
    // adicionar uma conversa preencherá o snapshot — sem spam.
    if (!bootstrappedRef.current) {
      if (conversations.length === 0) return;
      for (const c of conversations) {
        const t = c.lastMessageAt ? new Date(c.lastMessageAt).getTime() : 0;
        lastSeenRef.current.set(c.id, t);
      }
      bootstrappedRef.current = true;
      return;
    }

    for (const c of conversations) {
      const t = c.lastMessageAt ? new Date(c.lastMessageAt).getTime() : 0;
      const prev = lastSeenRef.current.get(c.id) ?? 0;
      // Heurística: lastMessageAt avançou E unreadCount > 0 → mensagem inbound.
      if (t > prev && c.unreadCount > 0) {
        const title = c.contactName || c.contactPhone || "Nova mensagem";
        const body = (c.lastMessageContent ?? "").slice(0, 140);
        notify("new_message", {
          title,
          body,
          tag: `conv-${c.id}`,
          onClick: () => onOpenConversation?.(c.id),
        });
      }
      lastSeenRef.current.set(c.id, t);
    }
  }, [conversations, notify, onOpenConversation]);
}
